import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const TOGGLES = [
  ["optional_2fa_enabled", "Offer optional 2FA platform-wide", "Users in optional roles may enable 2FA themselves."],
  ["email_otp_enabled", "Email OTP available", "One-time codes delivered to the registered email."],
  ["totp_enabled", "Authenticator App available", "TOTP apps such as Google Authenticator."],
  ["recovery_codes_enabled", "Recovery codes", "Single-use backup codes generated at enrollment."],
  ["recovery_requests_enabled", "Online account-recovery requests", "Users who lost all factors can request staff-verified recovery."],
];
const NUMS = [["otp_ttl_seconds", "OTP validity (seconds)"], ["resend_cooldown_seconds", "Resend cooldown (seconds)"],
  ["max_verify_attempts", "Max verification attempts per code"], ["max_sends_per_window", "Max codes per 15 minutes"],
  ["password_min_length", "Minimum password length"], ["login_max_attempts", "Failed sign-ins before lockout"],
  ["lockout_minutes", "Lockout duration (minutes)"], ["session_refresh_days", "Session lifetime (days)"]];

function PermissionMatrix({ roles }) {
  const [perms, setPerms] = useState(null);
  const [locked, setLocked] = useState({});
  useEffect(() => { api.get("/admin/role-permissions").then((r) => { setPerms(r.data.permissions); setLocked(r.data.locked); }); }, []);
  if (!perms) return null;
  const staff = Object.keys(roles).filter((r) => r !== "consumer");
  const toggle = (p, r) => setPerms({ ...perms, [p]: perms[p].includes(r) ? perms[p].filter((x) => x !== r) : [...perms[p], r] });
  const save = async () => { try { await api.put("/admin/role-permissions", perms); toast.success("Permissions saved"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Panel title="Role permissions" description="Backend RBAC. Super Admin is not an accounting approver unless explicitly granted twa.approve." testId="permission-matrix"
      actions={<Button size="sm" onClick={save} data-testid="save-permissions-button">Save permissions</Button>}>
      <div className="overflow-x-auto">
        <table className="text-xs w-full">
          <thead><tr><th className="text-left p-2">Permission</th>{staff.map((r) => <th key={r} className="p-2 font-medium">{roles[r]}</th>)}</tr></thead>
          <tbody>{Object.keys(perms).map((p) => (
            <tr key={p} className="border-t border-slate-100"><td className="p-2 font-mono">{p}</td>
              {staff.map((r) => <td key={r} className="text-center p-2"><Checkbox checked={perms[p].includes(r)} disabled={locked[p] === r || (["policy.manage", "settings.manage"].includes(p))} onCheckedChange={() => toggle(p, r)} data-testid={`perm-${p}-${r}`} /></td>)}</tr>
          ))}</tbody>
        </table>
      </div>
    </Panel>
  );
}

export default function SecurityPolicy() {
  const { requestReauth } = useAuth();
  const [data, setData] = useState(null);
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/admin/security-policy").then((r) => { setData(r.data); setP(r.data.policy); }); }, []);
  if (!p) return null;
  const save = async () => {
    try {
      const { data: r } = await api.put("/admin/security-policy", p);
      setP(r.policy);
      toast.success(`Policy saved${r.users_requiring_enrollment ? ` · ${r.users_requiring_enrollment} user(s) must enroll at next sign-in` : ""}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const staffRoles = Object.keys(data.roles).filter((r) => r !== "consumer");
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="System Settings" title="Security Policy" description="Enforced by the shared backend for the web portal and the consumer mobile app."
        actions={<Button onClick={save} data-testid="save-policy-button">Save policy</Button>} />
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Global OTP policy" testId="global-policy-panel">
          {TOGGLES.map(([k, label, hint]) => (
            <div key={k} className="flex items-center justify-between gap-4 py-3 border-b border-slate-100 last:border-0">
              <div><p className="text-sm font-medium">{label}</p><p className="text-xs text-slate-500">{hint}</p></div>
              <Switch checked={p[k]} onCheckedChange={(v) => setP({ ...p, [k]: v })} data-testid={`policy-${k}`} />
            </div>
          ))}
          <div className="flex items-center justify-between gap-4 py-3">
            <div><p className="text-sm font-medium">Default for newly created users</p><p className="text-xs text-slate-500">Applies to optional roles.</p></div>
            <select className="h-9 border rounded-md px-2 text-sm" value={p.default_new_user_2fa} onChange={(e) => setP({ ...p, default_new_user_2fa: e.target.value })} data-testid="policy-default-new-user">
              <option value="off">Off until user enables</option><option value="prompt">Prompt to enroll after sign-in</option>
            </select>
          </div>
        </Panel>
        <Panel title="Code limits" description="Kept within safe ranges by the backend.">
          <div className="grid grid-cols-2 gap-4">
            {NUMS.map(([k, label]) => (
              <div key={k} className="space-y-1"><p className="text-xs text-slate-500">{label} <span className="font-mono">[{data.limits[k].join("–")}]</span></p>
                <Input type="number" value={p[k]} min={data.limits[k][0]} max={data.limits[k][1]} onChange={(e) => setP({ ...p, [k]: Number(e.target.value) })} data-testid={`policy-${k}`} /></div>
            ))}
          </div>
          <p className="text-sm font-medium mt-6 mb-2">Roles allowed to perform administrative 2FA resets</p>
          <div className="flex flex-wrap gap-3">{staffRoles.map((r) => (
            <label key={r} className="flex items-center gap-2 text-sm"><Checkbox checked={p.admin_reset_roles.includes(r)} data-testid={`reset-role-${r}`}
              onCheckedChange={(c) => setP({ ...p, admin_reset_roles: c ? [...p.admin_reset_roles, r] : p.admin_reset_roles.filter((x) => x !== r) })} />{data.roles[r]}</label>
          ))}</div>
        </Panel>
      </div>
      <Panel title="Role-based 2FA policy" description="Required roles cannot disable 2FA; existing unenrolled users are signed out and must enroll at next sign-in." testId="role-policy-panel">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {Object.entries(data.roles).map(([r, label]) => (
            <div key={r} className="border border-slate-200 rounded-md p-3">
              <p className="text-sm font-medium mb-2">{label}</p>
              <div className="flex rounded-md border overflow-hidden text-xs">
                {["optional", "required"].map((v) => (
                  <button key={v} onClick={() => setP({ ...p, role_policies: { ...p.role_policies, [r]: v } })} data-testid={`role-policy-${r}-${v}`}
                    className={`flex-1 py-1.5 capitalize ${p.role_policies[r] === v ? "bg-amber-500 text-white font-semibold" : "bg-white text-slate-500 hover:bg-slate-50"}`}>{v}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Panel>
      <PermissionMatrix roles={data.roles} />
      <p className="text-xs text-slate-400">Sensitive changes may ask you to <button className="underline" onClick={requestReauth}>confirm your password</button>.</p>
    </div>
  );
}
